var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import qs from 'qs';
import { BadgeAlt as Badge } from '@cargomatic/ui';
import client from 'utils/rest/AdminClient';
import { AsyncCombobox } from './AsyncCombobox';
const SELECT_FIELDS = 'optimized_name company_name name first_name last_name _type email authorized is_broker';
function customerLabel(item) {
    if (item._type === 'Driver' || (!item.company_name && !item.name)) {
        const fullName = `${item.first_name || ''} ${item.last_name || ''}`.trim();
        return fullName || item.optimized_name || '';
    }
    return item.company_name || item.name || item.optimized_name || '';
}
function customerBadgeColor(item) {
    if ((item._type === 'Carrier' || item._type === 'Driver') && !item.authorized)
        return 'bg-red-50 text-red-700 border-red-200';
    switch (item._type) {
        case 'Shipper':
            return 'bg-emerald-50 text-emerald-700 border-emerald-200';
        case 'Carrier':
        case 'Driver':
            return 'bg-blue-50 text-blue-700 border-blue-200';
        case 'Vendor':
            return 'bg-purple-50 text-purple-700 border-purple-200';
        case 'Consignee':
            return 'bg-pink-50 text-pink-700 border-pink-200';
        default:
            return 'bg-gray-50 text-gray-700 border-gray-200';
    }
}
function customerBadgeText(item) {
    if ((item._type === 'Carrier' || item._type === 'Driver') && !item.authorized)
        return 'Unauthorized';
    return item._type || '';
}
function renderCustomerItem(item) {
    return (React.createElement("div", { className: "flex w-full items-center gap-2 min-w-0" },
        React.createElement(Badge, { variant: "outline", className: customerBadgeColor(item) }, customerBadgeText(item)),
        React.createElement("div", { className: "flex-1 min-w-0" },
            React.createElement("div", { className: "font-medium truncate" }, customerLabel(item)),
            item.email && (React.createElement("div", { className: "text-xs text-muted-foreground truncate" }, item.email))),
        item.is_broker && (React.createElement(Badge, { variant: "outline", className: "bg-orange-50 text-orange-700 border-orange-200 shrink-0" }, "Broker"))));
}
function makeFetcher(opts) {
    return async function fetchCustomers(name) {
        var _a;
        const query = {
            page: 1,
            per_page: 25,
            paginate: true,
            sort_by: 'optimized_name',
            order: 'asc',
            test: false,
            lean: true,
            select: SELECT_FIELDS,
            name,
        };
        if (opts.type)
            query._type = opts.type;
        if (opts.authorizedOnly)
            query.authorized = true;
        const res = (await client({
            method: 'GET',
            path: `admin/api/v2/users?${qs.stringify(query)}`,
        }));
        const items = ((_a = res === null || res === void 0 ? void 0 : res.entity) === null || _a === void 0 ? void 0 : _a.items) || [];
        // Authorized first (matches legacy customer-search sort).
        return [...items].sort((a, b) => {
            const aAuth = a.authorized !== false;
            const bAuth = b.authorized !== false;
            if (aAuth === bAuth)
                return customerLabel(a).localeCompare(customerLabel(b));
            return aAuth ? -1 : 1;
        });
    };
}
export function CustomerSearchCombobox(props) {
    const { type, authorizedOnly } = props, rest = __rest(props, ["type", "authorizedOnly"]);
    const loadOptions = React.useMemo(() => makeFetcher({ type, authorizedOnly }), [type, authorizedOnly]);
    return (React.createElement(AsyncCombobox, Object.assign({ placeholder: "Search for Customer" }, rest, { loadOptions: loadOptions, itemToLabel: customerLabel, itemToValue: (item) => item._id, renderItem: renderCustomerItem })));
}
